/**
 * First-entry coach mark (design v3, variant B): the first time a player
 * opens a game whose map has special rules, a coach mark points at the map
 * bar's ★ segment. Team games get none of their own (maintainer, 2026-11-03):
 * their rules live in the game menu's "Team play rules".
 *
 * Shown ONCE PER GAME per user (maintainer, 2026-11-03, overriding the v3
 * per-map keys): a game's rules never change mid-game, so one local key per
 * user + game covers it.
 * Stored through lib/storage (localStorage on web, SecureStore on native).
 */
import React from 'react'
import { getItem, setItem } from './storage'
import { reportError } from './reporting'
import { FirstEntryNoticeEnabledContext } from './firstEntryNoticeContext'

export type FirstEntryKind = 'map'

/** SecureStore keys allow only [A-Za-z0-9._-]. */
export function firstEntryKey(userId: string, gameId: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_')
  return `firstEntry.v1.${safe(userId)}.${safe(gameId)}`
}

/** Whether the game gets a coach mark: special-rules maps only, team game or not. */
export function firstEntryKind({ map }: { map: boolean }): FirstEntryKind | null {
  return map ? 'map' : null
}

export interface FirstEntryStore {
  get: (key: string) => Promise<string | null>
  set: (key: string, value: string) => Promise<void>
}

const defaultStore: FirstEntryStore = { get: getItem, set: setItem }

/** Has this user already seen the coach mark in this game? */
export async function hasSeenFirstEntry(userId: string, gameId: string, store: FirstEntryStore = defaultStore): Promise<boolean> {
  return (await store.get(firstEntryKey(userId, gameId))) === '1'
}

export async function markFirstEntrySeen(userId: string, gameId: string, store: FirstEntryStore = defaultStore): Promise<void> {
  await store.set(firstEntryKey(userId, gameId), '1')
}

export { FirstEntryNoticeEnabledContext }

/**
 * The coach mark to show now, or null. `eligible` is false while the
 * notice must wait (snapshot view, a blocking modal, a finished game).
 */
export function useFirstEntryNotice({ userId, gameId, map, eligible }: {
  userId: string | null | undefined
  gameId: string | null | undefined
  map: boolean
  eligible: boolean
}) {
  const enabled = React.useContext(FirstEntryNoticeEnabledContext)
  const kind = firstEntryKind({ map })
  const key = userId && gameId ? firstEntryKey(userId, gameId) : null
  // null = still reading storage; true = seen (or just dismissed).
  const [seen, setSeen] = React.useState<{ key: string; seen: boolean } | null>(null)
  React.useEffect(() => {
    if (!enabled || !key || !kind || !userId || !gameId) return
    let active = true
    hasSeenFirstEntry(userId, gameId)
      .then((value) => { if (active) setSeen({ key, seen: value }) })
      .catch((error) => {
        reportError(error, { tags: { surface: 'first-entry-notice', action: 'read' } })
        if (active) setSeen({ key, seen: true })
      })
    return () => { active = false }
  }, [enabled, key, kind, userId, gameId])
  const markSeen = React.useCallback(() => {
    if (!key || !userId || !gameId) return
    setSeen({ key, seen: true })
    markFirstEntrySeen(userId, gameId).catch((error) => reportError(error, { tags: { surface: 'first-entry-notice', action: 'write' } }))
  }, [key, userId, gameId])
  const show = enabled && eligible && !!kind && !!key && seen?.key === key && !seen.seen
  return { kind: show ? kind : null, markSeen }
}
